import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useToast } from './Toast';

export function ImageUpload({ value, onChange, path = 'uploads', label = 'Image', className = '' }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate type
    if (!file.type.startsWith('image/')) {
      showToast('Please upload an image file (JPG, PNG, WebP)', 'error');
      return;
    }

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image size must be less than 5MB', 'error');
      return;
    }

    setUploading(true);
    try {
      // Create a unique filename: timestamp_random_filename
      const filename = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, `${path}/${filename}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      
      onChange(url);
      showToast('Image uploaded successfully', 'success');
    } catch (error) {
      console.error('Upload error:', error);
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to upload image';
      
      if (error.code === 'storage/unauthorized') {
        errorMessage = 'You are not authorized to upload images. Please make sure you are logged in as an admin.';
      } else if (error.code === 'storage/canceled') {
        errorMessage = 'Upload was canceled.';
      } else if (error.code === 'storage/unknown') {
        errorMessage = 'An unknown error occurred. Please check that Firebase Storage is enabled in your Firebase Console.';
      } else if (error.message?.includes('CORS') || error.message?.includes('cors')) {
        errorMessage = 'CORS error: Firebase Storage may not be set up. Please enable Storage in Firebase Console and deploy storage rules.';
      } else {
        errorMessage = `Failed to upload image: ${error.message || 'Unknown error'}`;
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent triggering click on parent label if any
    onChange('');
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      <div 
        className={`relative border-2 border-dashed rounded-xl overflow-hidden transition-all duration-200 group ${
          value ? 'border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-[#c5a059] bg-white hover:bg-gray-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          disabled={uploading}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 disabled:cursor-not-allowed"
        />

        {value ? (
          <div className="relative w-full bg-gray-50 rounded-lg overflow-visible flex items-center justify-center" style={{ minHeight: '192px', padding: '12px' }}>
            <img 
              src={value} 
              alt="Uploaded preview" 
              className="max-w-full max-h-[400px] w-auto h-auto object-contain rounded-lg"
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-20 pointer-events-none rounded-lg">
               <p className="text-white font-medium flex items-center">
                 <Upload size={18} className="mr-2" /> Change Image
               </p>
            </div>
            <button
              onClick={handleRemove}
              className="absolute top-3 right-3 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all z-30 flex items-center justify-center min-w-[36px] min-h-[36px] border-2 border-white"
              title="Remove image"
              type="button"
              aria-label="Remove image"
            >
              <X size={18} strokeWidth={2.5} />
            </button>
          </div>
        ) : (
          <div className="h-48 flex flex-col items-center justify-center text-gray-400 p-6 text-center">
            {uploading ? (
              <div className="flex flex-col items-center text-[#c5a059]">
                <Loader2 size={32} className="animate-spin mb-2" />
                <span className="text-sm font-medium">Uploading...</span>
              </div>
            ) : (
              <>
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-yellow-50 group-hover:text-[#c5a059] transition-colors">
                  <ImageIcon size={24} />
                </div>
                <p className="text-sm font-medium text-gray-600 mb-1">Click to upload image</p>
                <p className="text-xs text-gray-400">JPG, PNG, WebP up to 5MB</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

